import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { cargarAcronimos } from './acronimos';
import { clearMyDepartmentsCache } from './useMyDepartment';
import { api } from './api';
import { identifyTenant, resetAnalytics } from './analytics';
import { ensurePushSubscribed } from './push';

export type TenantUser = {
  id: string;
  name: string;
  email: string;
  role: 'inspector' | 'master' | 'super-manager' | 'manager' | 'office' | 'tv';
  /** Alguien de oficina que TAMBIÉN es inspector (`users.is_inspector`): ve la escalera del modo inspector. */
  is_inspector?: boolean;
  can_make_payments?: boolean;
  /** Si la persona ya dibujó su firma (el trazo NUNCA viaja acá; se pide a /api/me/signature). */
  has_signature?: boolean;
  /** Ver el registro de cheques e imprimirlos. Lo decide el servidor: ver `puedeImprimirCheques`. */
  can_print_checks?: boolean;
  /** Firmante autorizado de la cuenta del banco: solo ellos suben firma y solo esas se imprimen. */
  can_sign_checks?: boolean;
  can_view_call_monitoring?: boolean;
  can_edit_risk_score?: boolean;
  can_make_templates?: boolean;
  /** Super Board: lo decide el backend (lista de Settings → Team; el master siempre). */
  can_view_super_board?: boolean;
  /** Templates (Management → Templates, mig 0553): la lista de su pestaña Access; el master siempre. */
  can_open_templates?: boolean;
  /** Agents (Management → Agents, mig 0562): la lista de su pestaña Access; el master siempre. */
  can_open_agents?: boolean;
  /** Proactive AI: el engranaje lo ve la lista de súper acceso (ai_super_access; el master siempre). */
  can_manage_ai?: boolean;
  /** Proactive AI: el "+ New assistant" y la pestaña Assistants: súper acceso o la lista "quién puede crear" (mig 0539). */
  can_create_ai_assistants?: boolean;
  must_change_password?: boolean;
  /** Nombre del usuario en Monday (assigned person) para la vista "Mías". */
  monday_assignee?: string | null;
  /** Número "call-from" para click-to-call (RingCentral RingOut). */
  call_from_number?: string | null;
  /** Toggle "Share screen" de Settings → Team (`users.can_share_screen`). El master pasa aunque esté en false. */
  can_share_screen?: boolean;
  /** Share screen: lo decide el backend (`shareHabilitadoPara`: env SCREEN_SHARE_ENABLED + activo + office+ + toggle). */
  share_screen_enabled?: boolean;
  /** Client Portal: el flag de infra (PORTAL_ENABLED). Apagado, el nav y Settings lo esconden. */
  portal_enabled?: boolean;
  /** Share screen solo para VER (inspectores, spec §4.11): `SCREEN_SHARE_INSPECTORS` + toggle. */
  share_view_enabled?: boolean;
  /** Record (grabar un video tipo Loom, spec §4.12): office+ con `SCREEN_RECORD_ENABLED` + toggle. */
  record_enabled?: boolean;
  /** Sede del reloj (`time_locations.name`), si /me la expone: decide el bitrate de Record por sede. */
  time_location_name?: string | null;
  company_name: string;
  company_slug: string;
};
export type Me =
  | { kind: 'tenant'; user: TenantUser }
  | { kind: 'platform'; user: { name: string; email: string } };

type AuthState = {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthCtx = createContext<AuthState>({
  me: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
});

/**
 * Ganchos que corren ANTES del `POST /api/auth/logout`, con la cookie todavía viva: el share screen
 * cierra su sesión en el servidor y suelta pantalla y micrófono (ShareProvider). Sin esto, `logout()`
 * mataba la cookie primero y el `/end` del share salía con 401. Tope de 1,5 s: salir nunca se cuelga.
 */
const beforeLogout = new Set<() => Promise<void> | void>();
export function onBeforeLogout(fn: () => Promise<void> | void): () => void {
  beforeLogout.add(fn);
  return () => { beforeLogout.delete(fn); };
}
async function runBeforeLogout(): Promise<void> {
  const jobs = [...beforeLogout].map(async (fn) => { try { await fn(); } catch { /* se sale igual */ } });
  await Promise.race([Promise.all(jobs), new Promise<void>((r) => { setTimeout(r, 1500); })]);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const m = await api.get<Me>('/api/auth/me');
      setMe(m);
      // Analytics: identifica con datos NO PII (id/rol/company). No-op sin PostHog.
      if (m.kind === 'tenant') {
        identifyTenant({ id: m.user.id, role: m.user.role, company_slug: m.user.company_slug });
        // Los acrónimos de la oficina, para que el primer mensaje ya salga con sus tooltips.
        void cargarAcronimos();
        // Push: si ya concedió permiso antes, re-suscribe en silencio (sobrevive deploys/expiración).
        void ensurePushSubscribed();
      }
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    await runBeforeLogout();
    await api.post('/api/auth/logout');
    setMe(null);
    resetAnalytics();
    // El departamento de trabajo es una preferencia por NAVEGADOR, no por usuario: sin esto,
    // en una PC compartida el siguiente que entra hereda el departamento del anterior y sus
    // claims abren en la pestaña equivocada.
    localStorage.removeItem('activeDept');
    clearMyDepartmentsCache();
  }

  useEffect(() => {
    refresh();
  }, []);

  return <AuthCtx.Provider value={{ me, loading, refresh, logout }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

/**
 * ¿Puede entrar al app de campo (/inspector)? El rol inspector siempre; alguien de oficina solo si
 * está marcado como inspector (pedido del dueño 2026-09-17: él hace ATRs con rol master). La misma
 * puerta que `requireInspector` en el backend.
 */
export function puedeModoInspector(me: Me | null): boolean {
  return me?.kind === 'tenant' && (me.user.role === 'inspector' || me.user.is_inspector === true);
}

/** El usuario de los televisores de la oficina: solo ve /tv (el board en modo TV). */
export function isTvUser(me: Me | null): boolean {
  return me?.kind === 'tenant' && me.user.role === 'tv';
}

const RANK = { tv: -1, inspector: 0, office: 1, manager: 2, 'super-manager': 3, master: 4 } as const;
export function hasRole(me: Me | null, min: keyof typeof RANK): boolean {
  return me?.kind === 'tenant' && RANK[me.user.role] >= RANK[min];
}

/**
 * Acceso a Call Monitoring. NO es por rol: es la lista que el dueño arma en Settings → Teams
 * ("solo las personas que yo decida"). El master entra siempre, para que la herramienta no
 * pueda quedar sin dueño.
 */
export function canSeeCallMonitoring(me: Me | null): boolean {
  return me?.kind === 'tenant' && (me.user.role === 'master' || me.user.can_view_call_monitoring === true);
}

/**
 * ¿Puedo hacer plantillas de email/SMS? Lo calcula el BACKEND y viaja en la sesión: la regla
 * (super-manager+ más las personas y equipos elegidos en Settings → Templates) tiene una sola
 * fuente. Acá no se re-deduce nada — si el botón decidiera por su cuenta, tarde o temprano
 * mostraría un botón que el servidor rechaza con 403.
 */
export function canMakeTemplates(me: Me | null): boolean {
  return me?.kind === 'tenant' && me.user.can_make_templates === true;
}

/**
 * Super Board (el tablero único de la office manager). Lo calcula el BACKEND con la misma puerta que sus
 * endpoints (la lista de Settings → Team; el master siempre): el ítem del menú y el 403 contestan igual.
 */
export function canSeeSuperBoard(me: Me | null): boolean {
  return me?.kind === 'tenant' && me.user.can_view_super_board === true;
}

/**
 * Templates (todas las plantillas en un lugar). Lo decide el BACKEND con la misma puerta que sus endpoints
 * (la lista de la pestaña Access de la sección; el master siempre): el ítem del menú y el 403 contestan igual.
 */
/** Agents (el portal de los bots). Lo decide el BACKEND por la lista de acceso de la sección; el master siempre. */
export function canOpenAgents(me: Me | null): boolean {
  return me?.kind === 'tenant' && me.user.can_open_agents === true;
}

export function canOpenTemplates(me: Me | null): boolean {
  return me?.kind === 'tenant' && me.user.can_open_templates === true;
}

/** Proactive AI: la sección la usa office+ (inspector y TV no); el engranaje, la lista de súper acceso. */
export function canUseAi(me: Me | null): boolean {
  return me?.kind === 'tenant' && me.user.role !== 'inspector' && me.user.role !== 'tv';
}
export function canManageAi(me: Me | null): boolean {
  return me?.kind === 'tenant' && me.user.can_manage_ai === true;
}
/** Crear y editar asistentes de Proactive AI: súper acceso o la lista "quién puede crear" (lo decide el backend). */
export function canCreateAiAssistants(me: Me | null): boolean {
  return me?.kind === 'tenant' && (me.user.can_manage_ai === true || me.user.can_create_ai_assistants === true);
}

/**
 * EJECUTAR PAGOS: emitir un cheque, imprimirlo, anularlo y ver el registro.
 *
 * Es el espejo EXACTO de `requireCanPay` del servidor (payments/routes.ts): manager+ **y** el
 * toggle `can_make_payments` de Settings → Team. Escrito una sola vez para que el botón y el 403
 * no puedan contestar distinto — si el botón aparece y el servidor dice que no, la persona cree
 * que el sistema está roto.
 *
 * OJO, no confundir con otras dos reglas parecidas que ya existen y que preguntan OTRA cosa:
 * "¿puede re-autorizar un pago ya hecho?" (super-manager o manager con el toggle) y "¿puede ver
 * los totales de plata del dashboard?" (el toggle o el master). Son tres preguntas distintas.
 */
export function puedeHacerPagos(me: Me | null): boolean {
  return me?.kind === 'tenant'
    && me.user.can_make_payments === true
    && ['master', 'super-manager', 'manager'].includes(me.user.role);
}

/**
 * VER el registro de cheques, mirar uno, IMPRIMIRLO, reimprimirlo, anularlo y corregirle el número.
 *
 * Son DOS conjuntos distintos (dueño 2026-10-27, leyendo sus dos mensajes juntos): GENERAR un cheque
 * es `can_make_payments` y nadie más (`puedeHacerPagos`); ver e imprimir es eso **o** un
 * super-manager del departamento de Payments — *"la sección de check issued quiero que super manager
 * de payments pueda verlo y desde ahí imprimirlos todos, pero no pueda generar cheques"*.
 *
 * No se calcula acá: el navegador no sabe en qué departamento está la persona. Lo resuelve el
 * servidor (`userCanPrintChecks`) y viaja en /me ya decidido, así el botón y el 403 no pueden
 * contestar distinto.
 */
export function puedeImprimirCheques(me: Me | null): boolean {
  return me?.kind === 'tenant' && me.user.can_print_checks === true;
}

/**
 * FIRMANTE AUTORIZADO de la cuenta del banco. No es lo mismo que poder pagar ni que poder imprimir:
 * es estar en la tarjeta de firmas del banco (dueño 2026-10-27: *"hay dos firmas en el banco, la mía
 * y la de Jessika"*). Solo esta gente sube su firma, y solo esas firmas se imprimen en un cheque.
 */
export function esFirmanteDeCheques(me: Me | null): boolean {
  return me?.kind === 'tenant' && me.user.can_sign_checks === true;
}

/** Config del semáforo de riesgo (⚙ de /risk). Mismo esquema: lista del dueño, master siempre. */
export function canEditRiskScore(me: Me | null): boolean {
  return me?.kind === 'tenant' && (me.user.role === 'master' || me.user.can_edit_risk_score === true);
}
